import { ref, onMounted, onBeforeUnmount, onActivated, nextTick } from 'vue'

export interface TableSplitterOptions {
  storageKey?: string
  defaultRatio?: number
  minTopHeight?: number
  minBottomHeight?: number
  splitterHeight?: number
  paginationHeight?: number
  onResize?: (topTableHeight: number) => void
}

export function useTableSplitter(options: TableSplitterOptions = {}) {
  const storageKey = options.storageKey || 'erp.table_split_ratio.default'
  const defaultRatio = options.defaultRatio ?? 0.38
  const minTopHeight = options.minTopHeight ?? 140
  const minBottomHeight = options.minBottomHeight ?? 0
  const splitterHeight = options.splitterHeight ?? 22
  const paginationHeight = options.paginationHeight ?? 52

  const isSubMenuCollapse = ref(true)
  const topHeight = ref(500)
  const bottomHeight = ref(260)
  const topTableHeight = ref(448)

  let dragStartTopHeight = 0
  let dragStartTotalHeight = 0

  const getAvailableTotalHeight = (): number => {
    const appMain = document.querySelector('.app-main')
    const filterContainer = document.querySelector('.filter-container')
    const appMainH = appMain?.clientHeight || (window.innerHeight - 45)
    const filterH = filterContainer?.clientHeight || 0
    // 留出底部留白与操作间距 30px
    return Math.max(260, appMainH - filterH - 30)
  }

  const getSavedRatio = (): number => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const val = parseFloat(saved)
        // 放宽有效比例区间至 2% ~ 90%
        if (!isNaN(val) && val >= 0.02 && val <= 0.90) {
          return val
        }
      }
    } catch (e) {
      console.warn('Failed to read split ratio from localStorage', e)
    }
    return defaultRatio
  }

  const applyLayout = (customRatio?: number) => {
    const totalH = getAvailableTotalHeight()
    if (isSubMenuCollapse.value) {
      // 收起状态：主表独占全部可用高度
      topHeight.value = totalH
      bottomHeight.value = 0
      topTableHeight.value = Math.max(120, totalH - paginationHeight)
    } else {
      // 展开状态：按比例切分主表与子表
      const currentRatio = customRatio ?? getSavedRatio()
      let bH = Math.round(totalH * currentRatio)
      bH = Math.max(minBottomHeight, Math.min(totalH - minTopHeight - splitterHeight, bH))
      const tH = totalH - bH - splitterHeight

      topHeight.value = tH
      bottomHeight.value = bH
      topTableHeight.value = Math.max(120, tH - paginationHeight)
    }

    if (options.onResize) {
      options.onResize(topTableHeight.value)
    }
  }

  const toggleSubMenuCollapse = () => {
    isSubMenuCollapse.value = !isSubMenuCollapse.value
    nextTick(() => {
      applyLayout()
    })
  }

  const handleSplitterDragStart = () => {
    dragStartTopHeight = topHeight.value
    dragStartTotalHeight = getAvailableTotalHeight()
  }

  const handleSplitterDragMove = ({ deltaY }: { deltaY: number }) => {
    let newTopH = dragStartTopHeight + deltaY
    const maxTopH = dragStartTotalHeight - minBottomHeight - splitterHeight
    newTopH = Math.max(minTopHeight, Math.min(maxTopH, newTopH))
    const newBottomH = Math.max(0, dragStartTotalHeight - newTopH - splitterHeight)

    topHeight.value = newTopH
    bottomHeight.value = newBottomH
    topTableHeight.value = Math.max(120, newTopH - paginationHeight)

    if (options.onResize) {
      options.onResize(topTableHeight.value)
    }
  }

  const handleSplitterDragEnd = () => {
    const totalH = dragStartTotalHeight || getAvailableTotalHeight()
    if (totalH > 0) {
      // Q1-B: 拖拽到最底部极限（子表高度 <= 25px）时，自动触发收起状态
      if (bottomHeight.value <= 25) {
        isSubMenuCollapse.value = true
        topHeight.value = totalH
        bottomHeight.value = 0
        topTableHeight.value = Math.max(120, totalH - paginationHeight)
        if (options.onResize) {
          options.onResize(topTableHeight.value)
        }
        // 注意：不将 0 写入 localStorage，保留上一次有效比例，确保点击“展开”时能复原
        return
      }

      const ratio = bottomHeight.value / totalH
      try {
        localStorage.setItem(storageKey, ratio.toFixed(4))
      } catch (e) {
        console.warn('Failed to persist split ratio', e)
      }
    }
  }

  const onWindowResize = () => {
    applyLayout()
  }

  onMounted(() => {
    nextTick(() => {
      applyLayout()
    })
    window.addEventListener('resize', onWindowResize)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onWindowResize)
  })

  onActivated(() => {
    setTimeout(() => {
      applyLayout()
    }, 100)
  })

  return {
    isSubMenuCollapse,
    topHeight,
    bottomHeight,
    topTableHeight,
    toggleSubMenuCollapse,
    handleSplitterDragStart,
    handleSplitterDragMove,
    handleSplitterDragEnd,
    applyLayout
  }
}
